/**
 * WhatsApp Message Template Editor
 * Meta-style template builder (name/language, content, buttons, live preview).
 * Used as the full-page "New Template" experience.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Alert, Snackbar,
  CircularProgress, Stack, Divider, Tooltip, Paper, Grid, FormControl,
  InputLabel, Select, Menu, Avatar, IconButton, Popover,
  RadioGroup, Radio, FormControlLabel, Checkbox, Collapse, Switch,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import {
  DeleteOutline, Campaign as CampaignIcon, CloudUploadOutlined,
  FormatBold, FormatItalic, StrikethroughS, Code, Add,
  EmojiEmotionsOutlined, ImageOutlined, VideocamOutlined, DescriptionOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import adminApi, { WhatsAppTemplateComponents, WhatsAppTemplateButton } from '@/services/admin.api';
import { usePlanGate } from '@/hooks/usePlanGate';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function extractVariables(text: string): number[] {
  const found = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) {
    found.add(parseInt(m[1] || '0', 10));
  }
  return Array.from(found).sort((a, b) => a - b);
}

/**
 * Silently normalize free text into the shape Meta's Cloud API actually
 * accepts, on every keystroke/paste, instead of catching it after the fact.
 * Applied to every free-text field sent to Meta as-is: body, TEXT header,
 * footer, and each carousel card's body.
 * - Line breaks are ALLOWED — and preserved — in a body (top-level or
 *   carousel card): Meta accepts them and WhatsApp renders them, and
 *   paragraphs are how a real template reads. This was previously collapsed
 *   to a space everywhere on the theory that a stray line break was what got
 *   a template auto-rejected as INVALID_FORMAT; that was a misdiagnosis (the
 *   rejections on record were carousels whose card bodies had no line breaks
 *   at all, while plenty of APPROVED bodies in the same WABA contain `\n`
 *   and even blank lines). A single-line field — TEXT header and footer —
 *   genuinely can't hold one, so there Enter still collapses to a space
 *   rather than being silently swallowed by a keypress that does nothing.
 * - Tabs are collapsed to a space everywhere: Meta rejects them and no
 *   template field has a legitimate use for one.
 * - `{{ 1 }}` is tightened to `{{1}}` — Meta requires no interior whitespace,
 *   and there's never a legitimate reason to want the spaced form, so this is
 *   a safe auto-fix rather than something worth interrupting the user for.
 *   (Bonus: `extractVariables` above only matches the tight form, so this
 *   also fixes variable/example-field detection for a spaced variable.)
 * - Runs of 5+ spaces are capped at Meta's 4-space limit.
 * Mirrored — not literally shared, this repo has no shared frontend/backend
 * package — in `whatsapp-template.controller.ts`'s `validateTextFormat`,
 * which is the real enforcement gate; keep both in sync if this list changes.
 */
export function sanitizeTemplateText(
  value: string,
  options: { allowLineBreaks?: boolean } = {}
): string {
  return (
    options.allowLineBreaks
      ? // CRLF/CR (a Windows or rich-text paste) is normalized to the bare \n
        // Meta stores, so the character counter and the 1024-char cap agree
        // with what actually gets submitted.
        value.replace(/\r\n?/g, '\n').replace(/\t/g, ' ')
      : value.replace(/[\n\r\t]/g, ' ')
  )
    .replace(/\{\{\s*(\d+)\s*\}\}/g, '{{$1}}')
    .replace(/ {5,}/g, '    ');
}

/**
 * The one Meta format rule that can't be silently auto-fixed — doing so would
 * mean inventing text the user didn't write — so it's surfaced as live inline
 * field feedback instead. Returns `null` when the text is fine.
 */
export function getVariableEdgeWarning(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/^\{\{\d+\}\}/.test(trimmed)) return "Can't start with a variable — add a word before it.";
  if (/\{\{\d+\}\}$/.test(trimmed)) return "Can't end with a variable — add a word after it.";
  return null;
}

/**
 * Meta requires `{{n}}` variables to be numbered sequentially from `{{1}}`
 * with no gaps — referencing `{{3}}` without `{{1}}`/`{{2}}` also present is
 * rejected. Mirrors `validateSequentialVariables` in
 * `whatsapp-template.controller.ts` (the real enforcement gate).
 */
export function getVariableSequenceWarning(text: string): string | null {
  const varNumbers = extractVariables(text); // already deduped + sorted
  if (varNumbers.length === 0) return null;
  const maxVar = Math.max(...varNumbers);
  const missing = Array.from({ length: maxVar }, (_, i) => i + 1).find((n) => !varNumbers.includes(n));
  return missing === undefined
    ? null
    : `Uses {{${maxVar}}} but is missing {{${missing}}} — variables must be numbered sequentially with no gaps.`;
}

/** Meta caps a MARKETING template's body at 10 emojis. Uses the standard
 *  Unicode "Extended_Pictographic" property — covers multi-codepoint emoji
 *  (skin tones, ZWJ sequences, flags) correctly, unlike a hand-rolled range. */
export function countEmojis(text: string): number {
  return (text.match(/\p{Extended_Pictographic}/gu) || []).length;
}

export type HeaderChoice = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';

/** One carousel card while editing (mutable, client-side). `id` is a local key
 *  only; media fields are filled by the per-card upload. Empty `carouselCards`
 *  means the template is NOT a carousel. */
export interface CarouselCardForm {
  id: string;
  headerType: 'IMAGE' | 'VIDEO';
  mediaHandle: string;
  mediaS3Key?: string;
  mediaMimeType?: string;
  mediaFileName: string;
  /** Local object URL of the just-uploaded file, for the live preview only. */
  mediaPreviewUrl?: string;
  bodyText: string;
  buttons: WhatsAppTemplateButton[];
}

export interface FormState {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  /** When non-empty, this is a CAROUSEL template (MARKETING/UTILITY only). */
  carouselCards: CarouselCardForm[];
  headerChoice: HeaderChoice;
  headerText: string;
  headerMediaHandle: string;
  headerMediaFileName: string;
  headerMediaUrl: string;
  /** Private S3 key from `uploadWhatsAppTemplateMedia`, used to obtain a Meta media id for sending. */
  headerMediaS3Key?: string;
  headerMediaMimeType?: string;
  bodyText: string;
  bodyExampleValues: string[];
  /** What each {{n}} body variable represents (e.g. "Order ID"), index 0 = {{1}}. */
  bodyVariableLabels?: string[];
  /** What the header's {{1}} represents, if the header is TEXT with a variable. */
  headerVariableLabel?: string;
  footerText: string;
  buttons: WhatsAppTemplateButton[];
  /** AUTHENTICATION only — mirrors Meta's own "Code delivery setup" flow. */
  authOtpType: 'COPY_CODE' | 'ONE_TAP' | 'ZERO_TAP';
  authAddSecurityRecommendation: boolean;
  /** Kept as a string for free typing in the field; parsed to an int (1-90) on submit. */
  authCodeExpirationMinutes: string;
  authCopyCodeButtonText: string;
  authAutofillButtonText: string;
  authApps: { package_name: string; signature_hash: string }[];
  authZeroTapTermsAccepted: boolean;
  /** Whether to send a custom `message_send_ttl_seconds` — off uses Meta's own 10-minute default. */
  authUseCustomValidity: boolean;
  /** Minutes, kept as a string for free typing; converted to seconds (30-900) on submit. */
  authValidityMinutes: string;
}

export function emptyForm(): FormState {
  return {
    name: '',
    language: 'en_US',
    category: 'UTILITY',
    carouselCards: [],
    headerChoice: 'NONE',
    headerText: '',
    headerMediaHandle: '',
    headerMediaFileName: '',
    headerMediaUrl: '',
    bodyText: '',
    bodyExampleValues: [],
    footerText: '',
    buttons: [],
    authOtpType: 'ZERO_TAP',
    authAddSecurityRecommendation: true,
    authCodeExpirationMinutes: '',
    authCopyCodeButtonText: 'Copy Code',
    authAutofillButtonText: 'Autofill',
    authApps: [],
    authZeroTapTermsAccepted: false,
    authUseCustomValidity: false,
    authValidityMinutes: '10',
  };
}

// ─── Presets ──────────────────────────────────────────────────────────────────
// Tailored for Gullivr.ai (B2B travel super-AI-agent: media generation, cross-selling, inventory).

/** Fields no MARKETING/UTILITY preset below needs to specify — `applyPreset`
 *  fills them in from `emptyForm()`'s defaults since none of these presets
 *  are AUTHENTICATION. */
type AuthFormFields =
  | 'authOtpType'
  | 'authAddSecurityRecommendation'
  | 'authCodeExpirationMinutes'
  | 'authCopyCodeButtonText'
  | 'authAutofillButtonText'
  | 'authApps'
  | 'authZeroTapTermsAccepted'
  | 'authUseCustomValidity'
  | 'authValidityMinutes';

export interface Preset {
  id: string;
  label: string;
  description: string;
  // `carouselCards` is omitted too — no preset ships a carousel; applyPreset
  // fills it from emptyForm()'s default ([]).
  form: Omit<FormState, 'name' | 'carouselCards' | AuthFormFields>;
}

export const PRESETS: Preset[] = [
  {
    id: 'blank',
    label: 'Blank template',
    description: 'Start from scratch.',
    form: emptyForm(),
  },
  {
    id: 'utility-status-update',
    label: 'Utility — Grievance / Ticket status update',
    description:
      'High-approval utility template for backend automation (e.g. enquiry/ticket status pushes).',
    form: {
      language: 'en_US',
      category: 'UTILITY',
      headerChoice: 'NONE',
      headerText: '',
      headerMediaHandle: '',
      headerMediaFileName: '',
      headerMediaUrl: '',
      bodyText:
        'Namaskar {{1}}, your request #{{2}} has been updated. Current status: {{3}}. You can track the detailed response here: {{4}}',
      bodyExampleValues: ['Rahul', 'GUL-10234', 'In Progress', 'https://gullivr.ai/track/GUL-10234'],
      footerText: '',
      buttons: [],
    },
  },
  {
    id: 'marketing-ai-media-teaser',
    label: 'Marketing — AI media teaser (lead gen)',
    description: 'Showcase AI-generated flyers/itineraries to drive platform engagement.',
    form: {
      language: 'en_US',
      category: 'MARKETING',
      headerChoice: 'IMAGE',
      headerText: '',
      headerMediaHandle: '',
      headerMediaFileName: '',
      headerMediaUrl: '',
      bodyText:
        'Hi {{1}}, spending hours building tour itineraries? Gullivr.ai just generated a complete, high-converting flyer and day-by-day itinerary for {{2}} in seconds. Let our AI agent handle your media generation so you can focus on closing deals.',
      bodyExampleValues: ['Wanderlust Travels', 'the upcoming Bali Summer packages'],
      footerText: '',
      buttons: [
        { type: 'URL', text: 'Generate Your Own', url: 'https://gullivr.ai/generate' },
        { type: 'QUICK_REPLY', text: 'See More Samples' },
      ],
    },
  },
  {
    id: 'marketing-cross-sell',
    label: 'Marketing — Cross-selling opportunity',
    description: 'Alert B2B partners about new inventory and margin opportunities.',
    form: {
      language: 'en_US',
      category: 'MARKETING',
      headerChoice: 'TEXT',
      headerText: 'Unlock New Margins',
      headerMediaHandle: '',
      headerMediaFileName: '',
      headerMediaUrl: '',
      bodyText:
        'Hello {{1}}, demand for {{2}} is surging this season. Expand your offerings instantly! Access exclusive B2B inventory on Gullivr.ai and let our AI discover the most profitable cross-selling opportunities for your existing client base.',
      bodyExampleValues: ['Rahul', 'European circuits'],
      footerText: '',
      buttons: [
        { type: 'URL', text: 'View Live Inventory', url: 'https://gullivr.ai/inventory/{{1}}' },
        { type: 'QUICK_REPLY', text: 'Talk to Sales' },
      ],
    },
  },
  {
    id: 'marketing-reengagement',
    label: 'Marketing — Re-engagement / platform value',
    description: 'Bring dormant agencies back with a "super AI agent" value pitch.',
    form: {
      language: 'en_US',
      category: 'MARKETING',
      headerChoice: 'VIDEO',
      headerText: '',
      headerMediaHandle: '',
      headerMediaFileName: '',
      headerMediaUrl: '',
      bodyText:
        'Hi {{1}}, ready to scale your travel business? Gullivr.ai is your one-stop platform and 24/7 AI agent. From smart inventory sourcing to automated marketing media, see how top agencies are boosting their margins by {{2}}% this quarter.',
      bodyExampleValues: ['Rahul', '30'],
      footerText: '',
      buttons: [
        { type: 'URL', text: 'Access Platform', url: 'https://gullivr.ai' },
        { type: 'QUICK_REPLY', text: 'Book a Demo' },
      ],
    },
  },
];

export const LANGUAGES = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
];

const MEDIA_ACCEPT: Record<string, string> = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

/** Meta's per-type ceilings (mirrors backend assertMetaMediaSize) — images 5MB,
 *  videos 16MB, documents 16MB platform cap. */
const MEDIA_MAX_SIZE_LABELS: Record<string, string> = {
  IMAGE: 'JPEG/PNG — large images are auto-compressed to fit WhatsApp’s 5MB limit',
  VIDEO: 'MP4 (H.264) up to 16MB',
  DOCUMENT: 'PDF up to 16MB',
};
const mediaMaxSizeLabel = (type: string) => MEDIA_MAX_SIZE_LABELS[type] || 'Up to 5MB';

const BUTTON_TYPE_LABELS: Record<WhatsAppTemplateButton['type'], string> = {
  // Never actually rendered — AUTHENTICATION templates configure their OTP
  // button via the dedicated "Code delivery setup" section, not this list —
  // present only so this Record stays exhaustive over WhatsAppTemplateButton['type'].
  OTP: 'OTP',
  QUICK_REPLY: 'Quick reply',
  URL: 'Visit website',
  PHONE_NUMBER: 'Call phone number',
  COPY_CODE: 'Copy offer code',
};

const COMMON_EMOJIS = [
  '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎',
  '🤩', '🥳', '😇', '🙂', '🙃', '😉', '🤔', '🤗',
  '😅', '😆', '😋', '😜', '🤤', '😴', '🥱', '😌',
  '🤝', '😢', '😭', '😡', '😱', '🤯', '🥺', '😬',
  '👍', '👎', '👏', '🙌', '🙏', '👋', '✌️', '🤞',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
  '🔥', '✨', '🎉', '🎊', '🎁', '🏆', '⭐', '🌟',
  '✅', '☑️', '❌', '⚠️', '📌', '📍', '⏰', '💡',
  '📞', '📱', '✉️', '💬', '💰', '🛍️', '🛒', '✈️',
  '🏨', '🌍', '🗺️', '📅', '🎯', '🚀', '💼', '📈',
];

// ─── Shared section / control building blocks ─────────────────────────────────

/** Card-style section wrapper — flat border, no shadow, generous padding. */
function Section({ title, optional, children }: { title: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, boxShadow: 'none', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        <Box component="span" fontWeight={700}>{title}</Box>
        {optional && <Box component="span" color="text.secondary"> · Optional</Box>}
      </Typography>
      {children}
    </Paper>
  );
}

/** Small, evenly-spaced toolbar icon button with a subtle hover background. */
function ToolbarButton({
  title, onClick, children,
}: { title: string; onClick: (e: React.MouseEvent<HTMLElement>) => void; children: React.ReactNode }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={(theme: Theme) => ({
          borderRadius: 1,
          color: theme.palette.text.secondary,
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
        })}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

/** Emoji picker for the message body — appends the chosen emoji to the body text. */
function EmojiPickerButton({ onSelect }: { onSelect: (emoji: string) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <ToolbarButton title="Emoji" onClick={(e) => setAnchor(e.currentTarget)}>
        <EmojiEmotionsOutlined fontSize="small" />
      </ToolbarButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          sx={{
            p: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 0.5,
            width: 336,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {COMMON_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => { onSelect(emoji); setAnchor(null); }}
              sx={{
                fontSize: 22,
                lineHeight: 1,
                borderRadius: 1,
                opacity: 1,
                filter: 'none',
                '&:hover': { bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.06) },
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ─── Preview bubble ──────────────────────────────────────────────────────────

function renderWithExamples(text: string, examples: string[]): string {
  return (text || '').replace(/\{\{(\d+)\}\}/g, (_, idx) => {
    const i = parseInt(idx, 10) - 1;
    return examples[i] || `[${idx}]`;
  });
}

/** Meta generates this fixed copy for AUTHENTICATION templates — the editor
 *  never sends free-text body/footer for this category, but the preview
 *  should still show what the customer will actually receive. */
function authPreviewText(form: FormState): { body: string; footer?: string } {
  let body = '{{1}} is your verification code.';
  if (form.authAddSecurityRecommendation) {
    body += ' For your security, do not share this code.';
  }
  const minutes = parseInt(form.authCodeExpirationMinutes || '', 10);
  const hasExpiry = Number.isInteger(minutes) && minutes >= 1 && minutes <= 90;
  return hasExpiry ? { body, footer: `This code expires in ${minutes} minutes.` } : { body };
}

const TemplatePreview: React.FC<{ form: FormState; mediaPreviewUrl?: string }> = ({ form, mediaPreviewUrl }) => {
  const isAuth = form.category === 'AUTHENTICATION';
  const authPreview = isAuth ? authPreviewText(form) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: '#e5ddd5',
        borderRadius: 2,
        maxWidth: 340,
      }}
    >
      <Paper sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
        {isAuth && authPreview ? (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {authPreview.body}
            </Typography>
            {authPreview.footer && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {authPreview.footer}
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  color: '#00a5f4',
                  fontSize: 14,
                  fontWeight: 500,
                  borderTop: '1px solid #f0f0f0',
                  py: 0.75,
                }}
              >
                {form.authCopyCodeButtonText || 'Copy Code'}
              </Box>
            </Box>
          </>
        ) : (
        <>
        {form.carouselCards.length === 0 && form.headerChoice === 'TEXT' && form.headerText && (
          <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
            {renderWithExamples(form.headerText, form.bodyExampleValues)}
          </Typography>
        )}
        {form.carouselCards.length === 0 &&
          (form.headerChoice === 'IMAGE' ||
          form.headerChoice === 'VIDEO' ||
          form.headerChoice === 'DOCUMENT') && (
          <Box
            sx={{
              mb: 1,
              height: 120,
              borderRadius: 1,
              bgcolor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              fontSize: 13,
              overflow: 'hidden',
            }}
          >
            {mediaPreviewUrl && form.headerChoice === 'IMAGE' ? (
              <img src={mediaPreviewUrl} alt="header" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : mediaPreviewUrl && form.headerChoice === 'VIDEO' ? (
              <video src={mediaPreviewUrl} controls style={{ width: '100%', height: '100%' }} />
            ) : (
              <>{form.headerMediaFileName || `${form.headerChoice} header`}</>
            )}
          </Box>
        )}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {renderWithExamples(form.bodyText, form.bodyExampleValues) || 'Message body…'}
        </Typography>
        {form.carouselCards.length === 0 && form.footerText && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {form.footerText}
          </Typography>
        )}
        {form.carouselCards.length === 0 && form.buttons.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {form.buttons.map((b, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: 'center',
                  color: '#00a5f4',
                  fontSize: 14,
                  fontWeight: 500,
                  borderTop: '1px solid #f0f0f0',
                  py: 0.75,
                }}
              >
                {b.text || `Button ${i + 1}`}
              </Box>
            ))}
          </Box>
        )}
        {form.carouselCards.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
            {form.carouselCards.map((card, ci) => (
              <Box
                key={card.id}
                sx={{
                  minWidth: 150,
                  maxWidth: 150,
                  flexShrink: 0,
                  border: '1px solid #f0f0f0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: 84,
                    bgcolor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    fontSize: 11,
                    overflow: 'hidden',
                  }}
                >
                  {card.mediaPreviewUrl && card.headerType === 'IMAGE' ? (
                    <img
                      src={card.mediaPreviewUrl}
                      alt={`card ${ci + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : card.mediaPreviewUrl && card.headerType === 'VIDEO' ? (
                    <video
                      src={card.mediaPreviewUrl}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <>{card.mediaFileName || `Card ${ci + 1} media`}</>
                  )}
                </Box>
                <Box sx={{ p: 0.75 }}>
                  <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                    {card.bodyText || 'Card text…'}
                  </Typography>
                  {card.buttons
                    .filter((b) => b.text.trim())
                    .map((b, bi) => (
                      <Box
                        key={bi}
                        sx={{
                          textAlign: 'center',
                          color: '#00a5f4',
                          fontSize: 12,
                          fontWeight: 500,
                          borderTop: '1px solid #f0f0f0',
                          py: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {b.text}
                      </Box>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        </>
        )}
      </Paper>
    </Paper>
  );
};

// ─── Editor ───────────────────────────────────────────────────────────────────

/**
 * Inverse of buildComponents(): hydrate the editor's FormState from a stored
 * template (draft editing). Field-by-field mirror — when buildComponents gains
 * a field, add its inverse here.
 */
export function formFromTemplate(t: {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  components: any;
  messageSendTtlSeconds?: number;
}): FormState {
  const base = emptyForm();
  const c = t.components || {};
  const header = c.header || null;
  const cards = c.carousel?.cards || [];

  const headerChoice: FormState['headerChoice'] =
    cards.length > 0 || !header
      ? 'NONE'
      : (['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type) ? header.type : 'NONE');

  // AUTHENTICATION: recover the OTP button config buildAuthComponents wrote.
  const otpButton = (c.buttons || []).find((b: any) => b.type === 'OTP');

  return {
    ...base,
    name: t.name,
    language: t.language,
    category: t.category,
    bodyText: c.body?.text || '',
    bodyExampleValues: c.body?.example?.body_text?.[0] || [],
    ...(c.body?.variableLabels?.length ? { bodyVariableLabels: [...c.body.variableLabels] } : {}),
    footerText: c.footer?.text || '',
    headerChoice,
    headerText: header?.type === 'TEXT' ? header.text || '' : '',
    ...(header?.headerVariableLabel ? { headerVariableLabel: header.headerVariableLabel } : {}),
    headerMediaHandle: header?.example?.header_handle?.[0] || '',
    headerMediaUrl: header?.headerMediaUrl || '',
    headerMediaFileName: header?.headerMediaFileName || '',
    ...(header?.headerMediaS3Key ? { headerMediaS3Key: header.headerMediaS3Key } : {}),
    ...(header?.headerMediaMimeType ? { headerMediaMimeType: header.headerMediaMimeType } : {}),
    buttons:
      t.category === 'AUTHENTICATION'
        ? []
        : (c.buttons || []).map((b: any) => ({
            type: b.type,
            text: b.text || '',
            ...(b.url ? { url: b.url } : {}),
            ...(b.phone_number ? { phone_number: b.phone_number } : {}),
            ...(b.example ? { example: b.example } : {}),
          })),
    carouselCards: cards.map((card: any, i: number) => ({
      id: `card_${i}_${card.header?.example?.header_handle?.[0]?.slice(-6) || i}`,
      headerType: card.header?.type === 'VIDEO' ? 'VIDEO' : 'IMAGE',
      mediaHandle: card.header?.example?.header_handle?.[0] || '',
      ...(card.header?.headerMediaS3Key ? { mediaS3Key: card.header.headerMediaS3Key } : {}),
      ...(card.header?.headerMediaMimeType
        ? { mediaMimeType: card.header.headerMediaMimeType }
        : {}),
      mediaFileName: card.header?.headerMediaFileName || '',
      bodyText: card.body?.text || '',
      buttons: (card.buttons || []).map((b: any) => ({
        type: b.type,
        text: b.text || '',
        ...(b.url ? { url: b.url } : {}),
        ...(b.phone_number ? { phone_number: b.phone_number } : {}),
      })),
    })),
    ...(otpButton
      ? {
          authOtpType: otpButton.otp_type || 'ZERO_TAP',
          authCopyCodeButtonText: otpButton.text || 'Copy Code',
          authAutofillButtonText: otpButton.autofill_text || 'Autofill',
          authApps: (otpButton.supported_apps || []).map((a: any) => ({
            package_name: a.package_name || '',
            signature_hash: a.signature_hash || '',
          })),
          authZeroTapTermsAccepted: !!otpButton.zero_tap_terms_accepted,
        }
      : {}),
    ...(t.messageSendTtlSeconds
      ? {
          authUseCustomValidity: true,
          authValidityMinutes: String(Math.round(t.messageSendTtlSeconds / 60)),
        }
      : {}),
  };
}

interface WhatsAppTemplateEditorProps {
  onCancel: () => void;
  /** `mode` tells the host page what actually happened (snackbar copy).
   *  'media' = a no-review media update (in-place edit, same-type file swap). */
  onSaved: (mode?: 'submitted' | 'draft' | 'media') => void;
  /** Editing an existing DRAFT: prefills the form and locks name+language so
   *  the draft self-upserts onto the same document. */
  initialTemplate?: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    components: any;
    messageSendTtlSeconds?: number;
  };
  /** Editing an existing APPROVED/REJECTED/PAUSED template IN PLACE (Meta
   *  re-reviews). Prefills + locks name/language like a draft edit, but saves via
   *  the template-edit endpoint instead of save-draft + submit-draft. */
  editExisting?: boolean;
}

/**
 * A stable signature of a template's CONTENT/structure — everything an edit
 * would have to send back to Meta for re-review. Deliberately EXCLUDES the media
 * file identity (handle/S3 key/filename) but INCLUDES the media TYPE
 * (`headerChoice` / card `headerType`): swapping a same-type file is not a content
 * change, but switching image↔video is. Used to decide, on an in-place edit,
 * between a Meta re-submit and a no-review media update.
 */
function contentSignature(f: FormState): string {
  const btns = (bs: WhatsAppTemplateButton[]) =>
    bs
      .filter((b) => b.text?.trim())
      .map((b) => ({
        type: b.type,
        text: b.text.trim(),
        url: (b as { url?: string }).url?.trim() || '',
        phone: (b as { phone_number?: string }).phone_number?.trim() || '',
      }));
  return JSON.stringify({
    category: f.category,
    headerChoice: f.headerChoice,
    headerText: f.headerText.trim(),
    bodyText: f.bodyText.trim(),
    footerText: f.footerText.trim(),
    buttons: btns(f.buttons),
    carousel: f.carouselCards.map((c) => ({
      headerType: c.headerType,
      bodyText: c.bodyText.trim(),
      buttons: btns(c.buttons),
    })),
  });
}

const WhatsAppTemplateEditor: React.FC<WhatsAppTemplateEditorProps> = ({
  onCancel,
  onSaved,
  initialTemplate,
  editExisting,
}) => {
  const { guard } = usePlanGate();
  const isExistingEdit = !!initialTemplate && !!editExisting;
  const isDraftEdit = !!initialTemplate && !editExisting;
  // Name + language are immutable once a template leaves Draft — locked for BOTH
  // draft-edit (self-upsert identity) and in-place edit (Meta forbids changing them).
  const isLockedIdentity = !!initialTemplate;
  const [presetId, setPresetId] = useState('blank');
  const [form, setForm] = useState<FormState>(() =>
    initialTemplate ? formFromTemplate(initialTemplate) : emptyForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── In-place edit: diff-and-route ────────────────────────────────────────
  // When editing an existing template we decide at save time whether a change
  // needs Meta (content/structure/media TYPE → re-review) or is just a same-type
  // media FILE swap (no re-review). We track the raw staged file(s) so the
  // media-only path can go through setHeaderMedia/setCarouselCardMedia, and
  // snapshot the original content so we can tell what actually changed.
  const [stagedHeaderFile, setStagedHeaderFile] = useState<File | null>(null);
  const [stagedCardFiles, setStagedCardFiles] = useState<Record<string, File>>({});
  const originalContentSig = React.useMemo(
    () => (initialTemplate ? contentSignature(formFromTemplate(initialTemplate)) : ''),
    [initialTemplate]
  );

  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyFieldRef = useRef<HTMLTextAreaElement>(null);

  const [addButtonAnchor, setAddButtonAnchor] = useState<null | HTMLElement>(null);
  // Collapsed by default — ONE_TAP/ZERO_TAP only work if the tenant already
  // has their own native Android app with WhatsApp's OTP SDK integrated,
  // which essentially none of this platform's tenants have. Keeping it
  // collapsed avoids presenting a dead-end as a normal setup step.
  const [authAdvancedOpen, setAuthAdvancedOpen] = useState(false);

  const applyPreset = (id: string) => {
    setPresetId(id);
    const preset = PRESETS.find((p) => p.id === id) || PRESETS[0]!;
    setMediaPreviewUrl('');
    setMediaError('');
    // No preset sets category to AUTHENTICATION, so none specify the auth
    // fields (see AuthFormFields) — fall back to emptyForm()'s defaults for
    // those so switching category to AUTHENTICATION afterwards still works.
    setForm({ ...emptyForm(), ...preset.form, name: form.name });
  };

  // Keep example-value and label slots in sync with detected body variables
  useEffect(() => {
    const vars = extractVariables(form.bodyText);
    setForm((prev) => {
      const values = vars.map((_, i) => prev.bodyExampleValues[i] || '');
      const labels = vars.map((_, i) => prev.bodyVariableLabels?.[i] || '');
      const valuesChanged =
        values.length !== prev.bodyExampleValues.length ||
        values.some((v, i) => v !== prev.bodyExampleValues[i]);
      const labelsChanged =
        labels.length !== (prev.bodyVariableLabels?.length || 0) ||
        labels.some((v, i) => v !== prev.bodyVariableLabels?.[i]);
      if (!valuesChanged && !labelsChanged) {
        return prev;
      }
      return {
        ...prev,
        bodyExampleValues: valuesChanged ? values : prev.bodyExampleValues,
        bodyVariableLabels: labelsChanged ? labels : (prev.bodyVariableLabels || []),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.bodyText]);

  // When opening an EXISTING template (draft/approved edit), its media lives in
  // storage — not as a just-picked local file — so fetch signed preview URLs so
  // the upload box and the live preview show the real image/video, not a blank.
  useEffect(() => {
    if (!initialTemplate) return;
    const { name, language, components } = initialTemplate;
    const headerType = components?.header?.type;
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType)) {
      adminApi
        .getWhatsAppTemplateHeaderMediaPreview(name, language)
        .then((r) => {
          if (r.previewUrl) setMediaPreviewUrl(r.previewUrl);
        })
        .catch(() => {});
    }
    if (components?.carousel?.cards?.length) {
      adminApi
        .getWhatsAppTemplateCarouselMediaPreview(name, language)
        .then((previews) => {
          setForm((p) => ({
            ...p,
            carouselCards: p.carouselCards.map((c, i) =>
              previews[i]?.previewUrl ? { ...c, mediaPreviewUrl: previews[i].previewUrl } : c
            ),
          }));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplate?.name, initialTemplate?.language]);

  const updateButton = (index: number, patch: Partial<WhatsAppTemplateButton>) => {
    setForm((prev) => {
      const buttons = [...prev.buttons];
      buttons[index] = { ...buttons[index], ...patch } as WhatsAppTemplateButton;
      return { ...prev, buttons };
    });
  };

  const addButton = (type: WhatsAppTemplateButton['type']) => {
    setForm((prev) => ({
      ...prev,
      buttons: [
        ...prev.buttons,
        type === 'COPY_CODE'
          ? { type, text: 'Copy offer code', example: '' }
          : { type, text: '' },
      ],
    }));
    setAddButtonAnchor(null);
  };

  const removeButton = (index: number) => {
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }));
  };

  // ─ AUTHENTICATION "App setup" (ONE_TAP/ZERO_TAP registered apps) ──────────
  const addAuthApp = () => {
    setForm((prev) => ({
      ...prev,
      authApps: [...(prev.authApps || []), { package_name: '', signature_hash: '' }],
    }));
  };

  const updateAuthApp = (index: number, patch: Partial<{ package_name: string; signature_hash: string }>) => {
    setForm((prev) => {
      const authApps = [...(prev.authApps || [])];
      authApps[index] = { ...authApps[index], ...patch } as { package_name: string; signature_hash: string };
      return { ...prev, authApps };
    });
  };

  const removeAuthApp = (index: number) => {
    setForm((prev) => ({ ...prev, authApps: (prev.authApps || []).filter((_, i) => i !== index) }));
  };

  const handleMediaFile = async (file: File) => {
    setMediaError('');
    setMediaUploading(true);
    try {
      const result = await adminApi.uploadWhatsAppTemplateMedia(file);
      setForm((p) => ({
        ...p,
        headerMediaHandle: result.handle,
        headerMediaFileName: file.name,
        headerMediaS3Key: result.s3Key,
        headerMediaMimeType: result.mimeType,
      }));
      // Keep the raw file so an in-place edit can route a same-type swap through
      // the no-review media endpoint.
      setStagedHeaderFile(file);
      setMediaPreviewUrl(URL.createObjectURL(file));
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setMediaError(err.response?.data?.error?.message || err.message || 'Failed to upload media');
      }
    } finally {
      setMediaUploading(false);
    }
  };

  // ── Carousel cards ──────────────────────────────────────────────────────
  const addCarouselCard = () => {
    setForm((p) => {
      if (p.carouselCards.length >= 10) return p;
      // A new card inherits the FIRST card's button structure (types/count) —
      // Meta requires every card to share one structure, so we keep them in step
      // by construction; the text is cleared for the author to fill in.
      const first = p.carouselCards[0];
      const buttons: WhatsAppTemplateButton[] = first
        ? first.buttons.map((b) => ({
            type: b.type,
            text: '',
            ...(b.type === 'URL' ? { url: '' } : {}),
            ...(b.type === 'PHONE_NUMBER' ? { phone_number: '' } : {}),
          }))
        : [];
      // Seed the media-type placeholder from an already-committed card (if
      // any) too — purely cosmetic until this card actually uploads
      // something (`getCardMediaAccept`/`handleCardMediaFile` key off other
      // cards' real `mediaHandle`, not this placeholder), but keeps a brand
      // new card's displayed state consistent with the rest from the start.
      const mediaReference = p.carouselCards.find((c) => c.mediaHandle);
      const card: CarouselCardForm = {
        id: `card_${Math.random().toString(36).slice(2, 9)}`,
        headerType: mediaReference?.headerType || 'IMAGE',
        mediaHandle: '',
        mediaFileName: '',
        bodyText: '',
        buttons,
      };
      return { ...p, carouselCards: [...p.carouselCards, card] };
    });
  };

  const removeCarouselCard = (id: string) =>
    setForm((p) => ({ ...p, carouselCards: p.carouselCards.filter((c) => c.id !== id) }));

  const updateCarouselCard = (id: string, patch: Partial<CarouselCardForm>) =>
    setForm((p) => ({
      ...p,
      carouselCards: p.carouselCards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));

  // ── Carousel card BUTTON STRUCTURE — synced across every card ──────────────
  // Meta requires every card to share the exact same button types in the exact
  // same order ("all cards must have the same components"); `addCarouselCard`
  // above already gets this right by construction for a brand-new card
  // (inherits card 1's structure). These three cover the gap that was left
  // open after cards exist: independently changing a button's type, or
  // adding/removing one, on a single card used to only affect that card —
  // meaning the UI let you build a structurally mismatched carousel that
  // would only fail at submit (or worse, at Meta, if the local check were
  // ever bypassed). Applying the change to every card's button at the same
  // index makes a mismatch impossible to create through this UI, the same
  // "prevent it, don't just catch it" approach used for the text-format
  // rules. Each card's own button TEXT and URL/phone-number VALUE stay
  // independently editable — only the shared TYPE/COUNT is kept in sync.
  const setButtonTypeOnAllCards = (bi: number, type: WhatsAppTemplateButton['type']) =>
    setForm((p) => ({
      ...p,
      carouselCards: p.carouselCards.map((c) => ({
        ...c,
        buttons: c.buttons.map((b, j) =>
          j === bi
            ? {
                type,
                text: b.text,
                ...(type === 'URL' ? { url: '' } : {}),
                ...(type === 'PHONE_NUMBER' ? { phone_number: '' } : {}),
              }
            : b
        ),
      })),
    }));

  const addButtonToAllCards = () =>
    setForm((p) => ({
      ...p,
      carouselCards: p.carouselCards.map((c) => ({
        ...c,
        buttons: c.buttons.length < 2 ? [...c.buttons, { type: 'QUICK_REPLY' as const, text: '' }] : c.buttons,
      })),
    }));

  const removeButtonFromAllCards = (bi: number) =>
    setForm((p) => ({
      ...p,
      carouselCards: p.carouselCards.map((c) => ({
        ...c,
        buttons: c.buttons.filter((_, j) => j !== bi),
      })),
    }));

  /** The already-committed media format other cards must match (see
   *  `handleCardMediaFile`) — `undefined` until some other card has actually
   *  uploaded media, at which point every other card's file picker narrows
   *  to that same type so a mismatch can't even be selected. */
  const getCardMediaReference = (excludeId: string) =>
    form.carouselCards.find((c) => c.id !== excludeId && c.mediaHandle);

  const getCardMediaAccept = (id: string): string => {
    const ref = getCardMediaReference(id);
    if (!ref) return 'image/jpeg,image/png,video/mp4';
    return ref.headerType === 'VIDEO' ? 'video/mp4' : 'image/jpeg,image/png';
  };

  const handleCardMediaFile = async (id: string, file: File) => {
    setMediaError('');

    // Meta requires every card's header to share the same media FORMAT
    // (all IMAGE or all VIDEO), same rule/same enforcement gap as the button
    // structure above. The first other card that's already uploaded media is
    // the reference — order-independent, so whichever card commits first
    // "locks" the format for the rest, matching the backend's structureKey
    // check (which always compares against array position 0).
    const derivedType: 'IMAGE' | 'VIDEO' = file.type.startsWith('video') ? 'VIDEO' : 'IMAGE';
    const referenceCard = getCardMediaReference(id);
    if (referenceCard && referenceCard.headerType !== derivedType) {
      setMediaError(
        `Every card must use the same media type — this carousel is already using ` +
          `${referenceCard.headerType === 'VIDEO' ? 'video' : 'image'} cards, so upload ` +
          `${referenceCard.headerType === 'VIDEO' ? 'a video' : 'an image'} here too.`
      );
      return;
    }

    try {
      const result = await adminApi.uploadWhatsAppTemplateMedia(file);
      updateCarouselCard(id, {
        mediaHandle: result.handle,
        mediaS3Key: result.s3Key,
        mediaMimeType: result.mimeType,
        mediaFileName: file.name,
        mediaPreviewUrl: URL.createObjectURL(file),
        headerType: derivedType,
      });
      setStagedCardFiles((p) => ({ ...p, [id]: file }));
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setMediaError(err.response?.data?.error?.message || err.message || 'Failed to upload card media');
      }
    }
  };

  /** Insert (or wrap the current selection with) the given snippet at the textarea's cursor, then restore focus/caret. */
  const insertAtCursor = (snippetBefore: string, snippetAfter: string = '') => {
    const textarea = bodyFieldRef.current;
    const start = textarea?.selectionStart ?? form.bodyText.length;
    const end = textarea?.selectionEnd ?? start;

    setForm((p) => {
      const selected = p.bodyText.slice(start, end);
      const bodyText = `${p.bodyText.slice(0, start)}${snippetBefore}${selected}${snippetAfter}${p.bodyText.slice(end)}`;
      return { ...p, bodyText };
    });

    const selectedLength = end - start;
    const caretPos = selectedLength > 0
      ? start + snippetBefore.length + selectedLength + snippetAfter.length
      : start + snippetBefore.length;

    requestAnimationFrame(() => {
      const el = bodyFieldRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caretPos, caretPos);
      }
    });
  };

  /** AUTHENTICATION templates: Meta generates the body/footer/header text itself
   *  from fixed OTP copy — there's nothing to build there. Only the delivery
   *  method (as a single OTP button) and the optional add-on flags are sent. */
  const buildAuthComponents = (): WhatsAppTemplateComponents => {
    const otpType = form.authOtpType || 'ZERO_TAP';
    const needsApps = otpType === 'ONE_TAP' || otpType === 'ZERO_TAP';

    const components: WhatsAppTemplateComponents = {
      buttons: [
        {
          type: 'OTP',
          otp_type: otpType,
          text: (form.authCopyCodeButtonText || 'Copy Code').trim(),
          ...(needsApps
            ? {
                autofill_text: (form.authAutofillButtonText || 'Autofill').trim(),
                supported_apps: (form.authApps || [])
                  .map((a) => ({ package_name: a.package_name.trim(), signature_hash: a.signature_hash.trim() }))
                  .filter((a) => a.package_name && a.signature_hash),
              }
            : {}),
          ...(otpType === 'ZERO_TAP' ? { zero_tap_terms_accepted: true } : {}),
        },
      ],
    };

    const authConfig: NonNullable<WhatsAppTemplateComponents['authConfig']> = {};
    if (form.authAddSecurityRecommendation) authConfig.addSecurityRecommendation = true;
    const expirationMinutes = parseInt(form.authCodeExpirationMinutes || '', 10);
    if (Number.isInteger(expirationMinutes) && expirationMinutes >= 1 && expirationMinutes <= 90) {
      authConfig.codeExpirationMinutes = expirationMinutes;
    }
    if (Object.keys(authConfig).length > 0) {
      components.authConfig = authConfig;
    }

    return components;
  };

  /** `message_send_ttl_seconds` is a top-level Meta field (sibling to
   *  `components`, not nested inside it) — see `authComponentsToMeta` on the
   *  backend. Undefined means "use Meta's own 10-minute default". */
  const buildAuthMessageSendTtlSeconds = (): number | undefined => {
    if (!form.authUseCustomValidity) return undefined;
    const minutes = parseInt(form.authValidityMinutes || '', 10);
    if (!Number.isInteger(minutes)) return undefined;
    const seconds = minutes * 60;
    return seconds >= 30 && seconds <= 900 ? seconds : undefined;
  };

  const buildComponents = (): WhatsAppTemplateComponents => {
    if (form.category === 'AUTHENTICATION') {
      return buildAuthComponents();
    }

    const body: NonNullable<WhatsAppTemplateComponents['body']> = { text: form.bodyText.trim() };
    const components: WhatsAppTemplateComponents = { body };

    // A carousel template is body (intro) + CAROUSEL only — Meta rejects a
    // top-level header/footer/buttons alongside cards. Skip all of them when
    // cards exist so the payload is valid.
    const isCarousel = form.carouselCards.length > 0;

    if (form.bodyExampleValues.length > 0 && form.bodyExampleValues.some((v) => v.trim())) {
      body.example = { body_text: [form.bodyExampleValues.map((v) => v.trim())] };
    }

    if (form.bodyVariableLabels?.length && form.bodyVariableLabels.some((l) => l.trim())) {
      body.variableLabels = form.bodyVariableLabels.map((l) => l.trim());
    }

    if (!isCarousel && form.headerChoice === 'TEXT' && form.headerText.trim()) {
      const headerVars = extractVariables(form.headerText);
      components.header = {
        type: 'TEXT',
        text: form.headerText.trim(),
        ...(headerVars.length > 0
          ? { example: { header_text: headerVars.map((v) => form.bodyExampleValues[v - 1] || '') } }
          : {}),
        ...(headerVars.includes(1) && form.headerVariableLabel?.trim()
          ? { headerVariableLabel: form.headerVariableLabel.trim() }
          : {}),
      };
    } else if (!isCarousel && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerChoice)) {
      components.header = {
        type: form.headerChoice as 'IMAGE' | 'VIDEO' | 'DOCUMENT',
        ...(form.headerMediaHandle.trim()
          ? { example: { header_handle: [form.headerMediaHandle.trim()] } }
          : {}),
        ...(form.headerMediaUrl.trim() ? { headerMediaUrl: form.headerMediaUrl.trim() } : {}),
        ...(form.headerMediaS3Key
          ? {
              headerMediaS3Key: form.headerMediaS3Key,
              headerMediaMimeType: form.headerMediaMimeType,
              ...(form.headerMediaFileName ? { headerMediaFileName: form.headerMediaFileName } : {}),
            }
          : {}),
      };
    }

    if (!isCarousel && form.footerText.trim()) {
      components.footer = { text: form.footerText.trim() };
    }

    if (!isCarousel && form.buttons.length > 0) {
      components.buttons = form.buttons
        .filter((b) => b.text.trim())
        .map((b) => ({
          type: b.type,
          text: b.text.trim(),
          ...(b.type === 'URL'
            ? {
                url: (b.url || '').trim(),
                // Only a dynamic ({{n}}) URL needs a sample for Meta's review —
                // a plain static URL button doesn't take an example at all.
                ...(/\{\{\s*\d+\s*\}\}/.test(b.url || '') && b.example
                  ? { example: b.example.trim() }
                  : {}),
              }
            : {}),
          ...(b.type === 'PHONE_NUMBER' ? { phone_number: (b.phone_number || '').trim() } : {}),
          ...(b.type === 'COPY_CODE' ? { example: (b.example || '').trim() } : {}),
        }));
    }

    // CAROUSEL — each card carries its own media header + body + (static) buttons.
    // The top-level body above is the intro; a carousel template has no top-level
    // media header (enforced in the UI by hiding it when cards exist).
    if (form.carouselCards.length > 0) {
      components.carousel = {
        cards: form.carouselCards.map((c) => ({
          header: {
            type: c.headerType,
            ...(c.mediaHandle.trim() ? { example: { header_handle: [c.mediaHandle.trim()] } } : {}),
            ...(c.mediaS3Key
              ? {
                  headerMediaS3Key: c.mediaS3Key,
                  headerMediaMimeType: c.mediaMimeType,
                  ...(c.mediaFileName ? { headerMediaFileName: c.mediaFileName } : {}),
                }
              : {}),
          },
          body: { text: c.bodyText.trim() },
          ...(c.buttons.filter((b) => b.text.trim()).length > 0
            ? {
                buttons: c.buttons
                  .filter((b) => b.text.trim())
                  .map((b) => ({
                    type: b.type,
                    text: b.text.trim(),
                    ...(b.type === 'URL' ? { url: (b.url || '').trim() } : {}),
                    ...(b.type === 'PHONE_NUMBER'
                      ? { phone_number: (b.phone_number || '').trim() }
                      : {}),
                  })),
              }
            : {}),
        })),
      };
    }

    return components;
  };

  // One save path, two exits: 'submit' sends to Meta (create, or for a draft
  // save-then-submit so edits are never lost if the Meta call fails); 'draft'
  // stores locally only. Validation is identical for both — a draft must be
  // submit-ready so its later submit can't 400 (backend enforces the same).
  const handleSave = async (mode: 'submit' | 'draft') => {
    try {
      setSaving(true);
      setError('');

      if (!form.name.trim()) {
        setError('Template name is required');
        return;
      }

      if (form.category === 'AUTHENTICATION') {
        const otpType = form.authOtpType || 'ZERO_TAP';
        if (otpType === 'ONE_TAP' || otpType === 'ZERO_TAP') {
          const apps = form.authApps || [];
          if (apps.length === 0) {
            setError(`${otpType === 'ONE_TAP' ? 'One-tap' : 'Zero-tap'} requires at least one app`);
            return;
          }
          for (const app of apps) {
            if (!app.package_name.trim()) {
              setError('Every app needs a package name');
              return;
            }
            if (app.signature_hash.trim().length !== 11) {
              setError('Every app\'s signature hash must be exactly 11 characters long');
              return;
            }
          }
        }
        if (otpType === 'ZERO_TAP' && !form.authZeroTapTermsAccepted) {
          setError('You must accept the zero-tap terms to submit this template');
          return;
        }
        if (form.authUseCustomValidity) {
          const minutes = parseInt(form.authValidityMinutes || '', 10);
          const seconds = minutes * 60;
          if (!Number.isInteger(minutes) || seconds < 30 || seconds > 900) {
            setError('Validity period must be between 1 and 15 minutes');
            return;
          }
        }
      } else if (!form.bodyText.trim()) {
        setError('Body text is required');
        return;
      } else {
        const bodyVarWarning = getVariableEdgeWarning(form.bodyText) || getVariableSequenceWarning(form.bodyText);
        if (bodyVarWarning) {
          setError(`Body: ${bodyVarWarning}`);
          return;
        }
        if (form.category === 'MARKETING' && countEmojis(form.bodyText) > 10) {
          setError(
            `The message body has ${countEmojis(form.bodyText)} emojis — Meta allows at most 10 in a Marketing template body.`
          );
          return;
        }
        if (form.carouselCards.length === 0 && form.headerChoice === 'TEXT') {
          const headerVarWarning =
            getVariableEdgeWarning(form.headerText) || getVariableSequenceWarning(form.headerText);
          if (headerVarWarning) {
            setError(`Header: ${headerVarWarning}`);
            return;
          }
        }
        const dynamicUrlButtons = form.buttons.filter(
          (b) => b.type === 'URL' && /\{\{\s*\d+\s*\}\}/.test(b.url || '')
        );
        if (dynamicUrlButtons.length > 1) {
          setError('Only one URL button may have a dynamic value');
          return;
        }
        for (const b of dynamicUrlButtons) {
          if (!/\{\{\s*\d+\s*\}\}\s*$/.test((b.url || '').trim())) {
            setError("A dynamic URL button's variable must be at the very end of the URL");
            return;
          }
          if (!b.example?.trim()) {
            setError('The dynamic URL button needs an example value for Meta\'s review');
            return;
          }
        }
      }

      const ttlSeconds = form.category === 'AUTHENTICATION' ? buildAuthMessageSendTtlSeconds() : undefined;

      const payload = {
        name: form.name.trim(),
        language: form.language,
        category: form.category,
        components: buildComponents(),
        ...(ttlSeconds !== undefined ? { messageSendTtlSeconds: ttlSeconds } : {}),
      };

      if (mode === 'draft') {
        await adminApi.saveWhatsAppTemplateDraft(payload);
        onSaved('draft');
      } else if (isExistingEdit) {
        // Diff-and-route: a content/structure/media-TYPE change goes to Meta for
        // re-review; a same-type media-FILE swap is a no-review media update.
        const contentChanged = contentSignature(form) !== originalContentSig;
        if (contentChanged) {
          // The current version keeps sending until Meta approves this edit.
          await adminApi.editWhatsAppTemplate(payload);
          onSaved('submitted');
        } else {
          const cardFiles = Object.entries(stagedCardFiles);
          if (!stagedHeaderFile && cardFiles.length === 0) {
            setError('No changes to save.');
            return;
          }
          if (stagedHeaderFile) {
            await adminApi.setWhatsAppTemplateHeaderMedia(payload.name, payload.language, stagedHeaderFile);
          }
          for (const [cardId, file] of cardFiles) {
            const idx = form.carouselCards.findIndex((c) => c.id === cardId);
            if (idx >= 0) {
              await adminApi.setWhatsAppTemplateCarouselMedia(payload.name, payload.language, idx, file);
            }
          }
          onSaved('media');
        }
      } else if (isDraftEdit) {
        // Persist the latest edits FIRST, then submit — if Meta rejects, the
        // draft still holds this exact version for fixing + resubmitting.
        await adminApi.saveWhatsAppTemplateDraft(payload);
        await adminApi.submitWhatsAppTemplateDraft(payload.name, payload.language);
        onSaved('submitted');
      } else {
        await adminApi.createWhatsAppTemplate(payload);
        onSaved('submitted');
      }
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) {
        setError(
          err.response?.data?.error?.message || err.message || 'Failed to save template'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => handleSave('submit');
  const handleSaveDraft = () => handleSave('draft');

  // Drives the in-place-edit primary button label + enablement: a content change
  // means a Meta re-submit; a same-type media swap means a no-review update.
  const editContentChanged = isExistingEdit && contentSignature(form) !== originalContentSig;
  const editMediaStaged = !!stagedHeaderFile || Object.keys(stagedCardFiles).length > 0;
  const editNoChanges = isExistingEdit && !editContentChanged && !editMediaStaged;

  const mediaSampleValue = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.headerChoice)
    ? form.headerChoice
    : 'NONE';

  const handleMediaSampleChange = (value: string) => {
    setMediaError('');
    if (value === 'NONE') {
      setForm((p) => ({
        ...p,
        headerChoice: p.headerText.trim() ? 'TEXT' : 'NONE',
        headerMediaHandle: '',
        headerMediaFileName: '',
      }));
      setMediaPreviewUrl('');
    } else {
      setForm((p) => ({
        ...p,
        headerChoice: value as HeaderChoice,
        headerText: '',
        headerMediaHandle: '',
        headerMediaFileName: '',
      }));
      setMediaPreviewUrl('');
    }
  };

  const COPY_CODE_COUNT = form.buttons.filter((b) => b.type === 'COPY_CODE').length;

  // Every error surfaced on this page — local pre-submit validation, the
  // backend's rejection, or a media/card-upload failure — funnels through one
  // bottom-right popup instead of each having its own in-page banner/caption
  // that pushes layout around. `error` (submit-time) takes priority over
  // `mediaError` (upload-time) when both happen to be set; dismissing always
  // clears whichever is currently showing, so the other still surfaces after.
  // No `onClose` on the Snackbar itself — MUI would otherwise also dismiss on
  // clickaway/Escape; omitting it means the only way to close this is the ×
  // on the Alert, which is exactly what was asked for.
  const activeError = error || mediaError;
  const dismissActiveError = () => (error ? setError('') : setMediaError(''));

  return (
    <Box>
      <Snackbar
        open={!!activeError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" variant="filled" onClose={dismissActiveError} sx={{ maxWidth: 420 }}>
          {activeError}
        </Alert>
      </Snackbar>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="preset-label">Start from a preset</InputLabel>
            <Select
              labelId="preset-label"
              label="Start from a preset"
              value={presetId}
              onChange={(e) => applyPreset(e.target.value)}
            >
              {PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={2.5}>
            {/* Top preview chip */}
            <Paper
              variant="outlined"
              sx={{
                p: 2, borderRadius: 2, boxShadow: 'none', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}
            >
              <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', width: 40, height: 40, borderRadius: 1.5 }}>
                <CampaignIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {form.name || 'your_template_name'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {LANGUAGES.find((l) => l.code === form.language)?.label || form.language}
                  {' · '}
                  {form.category.charAt(0) + form.category.slice(1).toLowerCase()}
                </Typography>
              </Box>
            </Paper>

            {/* Template name and language */}
            <Section title="Template name and language">
              <Stack spacing={2}>
                <TextField
                  label="Name your template"
                  size="small"
                  fullWidth
                  placeholder="e.g. order_update_v1"
                  value={form.name}
                  // Locked when editing an existing template (draft or approved) —
                  // name+language are the template's identity (and immutable on Meta),
                  // so renaming would fork a new template rather than edit this one.
                  disabled={isLockedIdentity}
                  inputProps={{ maxLength: 512 }}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                    }))
                  }
                  helperText={
                    isLockedIdentity ? 'Name is locked — it identifies this template' : `${form.name.length}/512`
                  }
                />
                <Stack direction="row" spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="lang-label">Language</InputLabel>
                    <Select
                      labelId="lang-label"
                      label="Language"
                      value={form.language}
                      disabled={isLockedIdentity}
                      onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
                    >
                      {LANGUAGES.map((l) => (
                        <MenuItem key={l.code} value={l.code}>
                          {l.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="category-label">Category</InputLabel>
                    <Select
                      labelId="category-label"
                      label="Category"
                      value={form.category}
                      onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as FormState['category'] }))}
                    >
                      <MenuItem value="UTILITY">Utility</MenuItem>
                      <MenuItem value="MARKETING">Marketing</MenuItem>
                      <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Section>

            {/* Code delivery setup — AUTHENTICATION only. Meta generates the
                body/header/footer text itself; there's nothing to write here,
                only the OTP delivery method to configure. */}
            {form.category === 'AUTHENTICATION' && (
              <Section title="Code delivery setup">
                <Stack spacing={2}>
                  <FormControl>
                    <RadioGroup
                      value={form.authOtpType || 'ZERO_TAP'}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, authOtpType: e.target.value as FormState['authOtpType'] }))
                      }
                    >
                      <FormControlLabel
                        value="ZERO_TAP"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">Zero-tap auto-fill</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sends the code without the customer tapping anything. Falls back to
                              auto-fill or copy code if zero-tap isn't possible.
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="ONE_TAP"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">One-tap auto-fill</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sends the code to your app when the customer taps the button.
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="COPY_CODE"
                        control={<Radio size="small" />}
                        label={
                          <Box>
                            <Typography variant="body2">Copy code</Typography>
                            <Typography variant="caption" color="text.secondary">
                              The customer copies and pastes the code into your app.
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    label="Copy code button text"
                    size="small"
                    fullWidth
                    value={form.authCopyCodeButtonText || ''}
                    onChange={(e) => setForm((p) => ({ ...p, authCopyCodeButtonText: e.target.value }))}
                  />

                  {(form.authOtpType === 'ONE_TAP' || form.authOtpType === 'ZERO_TAP') && (
                    <Box>
                      <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                        {form.authOtpType === 'ONE_TAP' ? 'One-tap' : 'Zero-tap'} only works if you
                        already have your own native Android app with WhatsApp's OTP SDK integrated —
                        it won't do anything for a business that only uses WhatsApp itself. If that's
                        not you, Copy code is the right choice.
                      </Alert>
                      <Button
                        size="small"
                        onClick={() => setAuthAdvancedOpen((v) => !v)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {authAdvancedOpen ? 'Hide' : 'Show'} advanced app setup
                      </Button>
                      <Collapse in={authAdvancedOpen}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <TextField
                            label="Autofill button text"
                            size="small"
                            fullWidth
                            value={form.authAutofillButtonText || ''}
                            onChange={(e) => setForm((p) => ({ ...p, authAutofillButtonText: e.target.value }))}
                          />

                          <Divider />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                              App setup
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                              You can add up to 5 apps. Each signature hash must be exactly 11 characters.
                            </Typography>
                            <Stack spacing={1.5}>
                              {(form.authApps || []).map((app, i) => (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} key={i} alignItems="center">
                                  <TextField
                                    label="Package name"
                                    size="small"
                                    fullWidth
                                    placeholder="com.example.myapplication"
                                    value={app.package_name}
                                    onChange={(e) => updateAuthApp(i, { package_name: e.target.value })}
                                  />
                                  <TextField
                                    label="App signature hash"
                                    size="small"
                                    fullWidth
                                    value={app.signature_hash}
                                    inputProps={{ maxLength: 11 }}
                                    helperText={`${app.signature_hash.length}/11`}
                                    onChange={(e) => updateAuthApp(i, { signature_hash: e.target.value })}
                                  />
                                  <IconButton size="small" onClick={() => removeAuthApp(i)} sx={{ color: 'error.main' }}>
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ))}
                              {(form.authApps || []).length < 5 && (
                                <Box>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Add fontSize="small" />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                                    onClick={addAuthApp}
                                  >
                                    Add another app
                                  </Button>
                                </Box>
                              )}
                            </Stack>
                          </Box>

                          {form.authOtpType === 'ZERO_TAP' && (
                            <>
                              <Divider />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={!!form.authZeroTapTermsAccepted}
                                    onChange={(e) =>
                                      setForm((p) => ({ ...p, authZeroTapTermsAccepted: e.target.checked }))
                                    }
                                  />
                                }
                                label={
                                  <Typography variant="caption">
                                    By selecting zero-tap, I understand this is subject to WhatsApp's
                                    Business Terms of Service and that the code will be automatically
                                    filled in on the customer's behalf.
                                  </Typography>
                                }
                              />
                            </>
                          )}
                        </Stack>
                      </Collapse>
                    </Box>
                  )}

                  <TextField
                    label="Code expiration (minutes) · Optional"
                    size="small"
                    type="number"
                    fullWidth
                    placeholder="e.g. 5"
                    value={form.authCodeExpirationMinutes || ''}
                    inputProps={{ min: 1, max: 90 }}
                    helperText="1-90 minutes. Adds an expiry line to the message footer."
                    onChange={(e) => setForm((p) => ({ ...p, authCodeExpirationMinutes: e.target.value }))}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!form.authAddSecurityRecommendation}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, authAddSecurityRecommendation: e.target.checked }))
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Add security recommendation ("For your security, do not share this code.")
                      </Typography>
                    }
                  />

                  <Divider />
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Set custom validity period
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          How long Meta keeps trying to deliver this before giving up (you aren't
                          billed if it expires undelivered). Defaults to 10 minutes if left off.
                        </Typography>
                      </Box>
                      <Switch
                        checked={!!form.authUseCustomValidity}
                        onChange={(e) => setForm((p) => ({ ...p, authUseCustomValidity: e.target.checked }))}
                      />
                    </Stack>
                    {form.authUseCustomValidity && (
                      <TextField
                        label="Validity period (minutes)"
                        size="small"
                        type="number"
                        fullWidth
                        sx={{ mt: 1.5 }}
                        value={form.authValidityMinutes || ''}
                        inputProps={{ min: 1, max: 15 }}
                        helperText="1-15 minutes (Meta's allowed range for authentication templates)."
                        onChange={(e) => setForm((p) => ({ ...p, authValidityMinutes: e.target.value }))}
                      />
                    )}
                  </Box>
                </Stack>
              </Section>
            )}

            {/* Content */}
            {form.category !== 'AUTHENTICATION' && (
            <Section title="Content">
              <Stack spacing={2}>
                {form.carouselCards.length > 0 && (
                  <Alert severity="info" sx={{ fontSize: 13 }}>
                    This is a <strong>carousel</strong> template. Only the message body below is used —
                    as the intro shown above the cards. Meta does not allow a top-level header, footer
                    or buttons on a carousel, so those are hidden here; the media and buttons live on
                    each card in the <strong>Carousel</strong> section below.
                  </Alert>
                )}
                {form.carouselCards.length === 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel id="media-sample-label">Media sample · Optional</InputLabel>
                  <Select
                    labelId="media-sample-label"
                    label="Media sample · Optional"
                    value={mediaSampleValue}
                    onChange={(e) => handleMediaSampleChange(e.target.value)}
                  >
                    <MenuItem value="NONE">None</MenuItem>
                    <MenuItem value="IMAGE">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ImageOutlined fontSize="small" color="action" /> <span>Image</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="VIDEO">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <VideocamOutlined fontSize="small" color="action" /> <span>Video</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="DOCUMENT">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DescriptionOutlined fontSize="small" color="action" /> <span>Document</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
                )}

                {form.carouselCards.length === 0 && mediaSampleValue !== 'NONE' && (
                  <Box>
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file) handleMediaFile(file);
                      }}
                      sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: 'action.hover',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept={MEDIA_ACCEPT[mediaSampleValue]}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleMediaFile(file);
                        }}
                      />
                      {mediaUploading ? (
                        <CircularProgress size={24} />
                      ) : form.headerMediaFileName ? (
                        <Stack alignItems="center" spacing={0.5}>
                          <CloudUploadOutlined color="success" />
                          <Typography variant="body2">{form.headerMediaFileName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Click or drop to replace
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack alignItems="center" spacing={0.5}>
                          <CloudUploadOutlined color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Drag &amp; drop a sample {mediaSampleValue.toLowerCase()}, or click to upload
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mediaMaxSizeLabel(mediaSampleValue)}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                    <TextField
                      label="Header media URL (for sending)"
                      size="small"
                      fullWidth
                      sx={{ mt: 2 }}
                      placeholder="https://example.com/path/to/media.mp4"
                      value={form.headerMediaUrl}
                      onChange={(e) => setForm((p) => ({ ...p, headerMediaUrl: e.target.value }))}
                      helperText="Used when sending — separate from the sample above (Meta review only)."
                    />
                  </Box>
                )}

                {form.carouselCards.length === 0 && mediaSampleValue === 'NONE' && (
                  <TextField
                    label="Header · Optional"
                    size="small"
                    fullWidth
                    placeholder="Add a title or short description"
                    value={form.headerText}
                    inputProps={{ maxLength: 60 }}
                    onChange={(e) => {
                      const value = sanitizeTemplateText(e.target.value);
                      setForm((p) => ({
                        ...p,
                        headerText: value,
                        headerChoice: value.trim() ? 'TEXT' : 'NONE',
                      }));
                    }}
                    error={
                      !!(getVariableEdgeWarning(form.headerText) || getVariableSequenceWarning(form.headerText))
                    }
                    helperText={
                      getVariableEdgeWarning(form.headerText) ||
                      getVariableSequenceWarning(form.headerText) ||
                      `${form.headerText.length}/60`
                    }
                  />
                )}

                {mediaSampleValue === 'NONE' && extractVariables(form.headerText).includes(1) && (
                  <TextField
                    label="What does header {{1}} represent?"
                    size="small"
                    fullWidth
                    placeholder="e.g. Customer name"
                    value={form.headerVariableLabel || ''}
                    onChange={(e) => setForm((p) => ({ ...p, headerVariableLabel: e.target.value }))}
                    helperText="Used to auto-suggest the variable mapping in Create Campaign. Not sent to Meta."
                  />
                )}

                <Box>
                  <TextField
                    label="Body"
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder="Write your message…"
                    value={form.bodyText}
                    inputProps={{ maxLength: 1024 }}
                    inputRef={bodyFieldRef}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        bodyText: sanitizeTemplateText(e.target.value, { allowLineBreaks: true }),
                      }))
                    }
                    error={
                      !!(getVariableEdgeWarning(form.bodyText) || getVariableSequenceWarning(form.bodyText)) ||
                      (form.category === 'MARKETING' && countEmojis(form.bodyText) > 10)
                    }
                    helperText={
                      getVariableEdgeWarning(form.bodyText) ||
                      getVariableSequenceWarning(form.bodyText) ||
                      (form.category === 'MARKETING' && countEmojis(form.bodyText) > 10
                        ? `${countEmojis(form.bodyText)} emojis — Meta allows at most 10 in a Marketing template body.`
                        : undefined)
                    }
                  />
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                    <EmojiPickerButton onSelect={(emoji) => insertAtCursor(emoji)} />
                    <ToolbarButton title="Bold" onClick={() => insertAtCursor('*', '*')}>
                      <FormatBold fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton title="Italic" onClick={() => insertAtCursor('_', '_')}>
                      <FormatItalic fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton title="Strikethrough" onClick={() => insertAtCursor('~', '~')}>
                      <StrikethroughS fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton title="Code" onClick={() => insertAtCursor('```', '```')}>
                      <Code fontSize="small" />
                    </ToolbarButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                    <Button
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                      onClick={() => {
                        const next = extractVariables(form.bodyText).length + 1;
                        insertAtCursor(`{{${next}}}`);
                      }}
                    >
                      + Add variable
                    </Button>
                    <Box flexGrow={1} />
                    <Typography variant="caption" color="text.secondary">
                      {form.bodyText.length}/1024
                    </Typography>
                  </Stack>
                </Box>

                {extractVariables(form.bodyText).length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      Example values for variables (required by Meta for review)
                    </Typography>
                    {extractVariables(form.bodyText).map((v, i) => (
                      <Stack key={v} direction="row" spacing={1}>
                        <TextField
                          label={`Example for {{${v}}}`}
                          size="small"
                          fullWidth
                          value={form.bodyExampleValues[i] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => {
                              const values = [...p.bodyExampleValues];
                              values[i] = value;
                              return { ...p, bodyExampleValues: values };
                            });
                          }}
                        />
                        <TextField
                          label={`What is {{${v}}}?`}
                          size="small"
                          fullWidth
                          placeholder="e.g. Order ID"
                          value={form.bodyVariableLabels?.[i] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((p) => {
                              const labels = [...(p.bodyVariableLabels || [])];
                              labels[i] = value;
                              return { ...p, bodyVariableLabels: labels };
                            });
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}

                {form.carouselCards.length === 0 && (
                <TextField
                  label="Footer · Optional"
                  size="small"
                  fullWidth
                  placeholder="Add a short line of text"
                  value={form.footerText}
                  inputProps={{ maxLength: 60 }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, footerText: sanitizeTemplateText(e.target.value) }))
                  }
                  helperText={`${form.footerText.length}/60`}
                />
                )}
              </Stack>
            </Section>
            )}

            {/* Carousel — MARKETING/UTILITY only. When cards exist the template
                renders as a carousel: the body above is the intro, and each card
                carries its own media + text + buttons. v1 cards are static. */}
            {form.category !== 'AUTHENTICATION' && (
            <Section title="Carousel" optional>
              <Stack spacing={1.5}>
                {form.carouselCards.length === 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Add up to 10 swipeable cards, each with its own image or video, a short
                      line of text and buttons. All cards must share the same structure.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={addCarouselCard}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Make this a carousel
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <Tooltip
                        arrow
                        title={
                          'Carousel template — the top-level header/media is not used (the intro is ' +
                          'body-only). Cards send static content (no {{n}} variables) for now. Meta ' +
                          "requires every card to use the same button types in the same order, so " +
                          "changing a button's type, or adding/removing one, applies to all cards at " +
                          "once — each card's own button text and link/phone number stay independent."
                        }
                      >
                        <InfoOutlined sx={{ fontSize: 16, cursor: 'help' }} />
                      </Tooltip>
                      <Typography variant="caption">How carousel templates work</Typography>
                    </Box>
                    {form.carouselCards.map((card, idx) => (
                      <Box
                        key={card.id}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600} fontSize={14}>
                              Card {idx + 1}
                            </Typography>
                            <IconButton size="small" onClick={() => removeCarouselCard(card.id)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Stack>

                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUploadOutlined />}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            {card.mediaFileName || 'Upload image / video'}
                            <input
                              hidden
                              type="file"
                              accept={getCardMediaAccept(card.id)}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.currentTarget.value = '';
                                if (f) void handleCardMediaFile(card.id, f);
                              }}
                            />
                          </Button>

                          <TextField
                            label="Card text"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={card.bodyText}
                            inputProps={{ maxLength: 160 }}
                            onChange={(e) =>
                              updateCarouselCard(card.id, {
                                bodyText: sanitizeTemplateText(e.target.value, { allowLineBreaks: true }),
                              })
                            }
                            helperText={`${card.bodyText.length}/160`}
                          />

                          {card.buttons.map((b, bi) => (
                            <Stack key={bi} direction="row" spacing={1} alignItems="center">
                              <TextField
                                select
                                size="small"
                                label="Button"
                                value={b.type}
                                sx={{ width: 140 }}
                                onChange={(e) =>
                                  setButtonTypeOnAllCards(bi, e.target.value as WhatsAppTemplateButton['type'])
                                }
                              >
                                <MenuItem value="QUICK_REPLY">Quick reply</MenuItem>
                                <MenuItem value="URL">URL</MenuItem>
                                <MenuItem value="PHONE_NUMBER">Call phone number</MenuItem>
                              </TextField>
                              <TextField
                                size="small"
                                label="Text"
                                value={b.text}
                                onChange={(e) =>
                                  updateCarouselCard(card.id, {
                                    buttons: card.buttons.map((x, j) =>
                                      j === bi ? { ...x, text: e.target.value } : x
                                    ),
                                  })
                                }
                              />
                              {b.type === 'URL' && (
                                <TextField
                                  size="small"
                                  label="URL"
                                  fullWidth
                                  value={b.url ?? ''}
                                  onChange={(e) =>
                                    updateCarouselCard(card.id, {
                                      buttons: card.buttons.map((x, j) =>
                                        j === bi ? { ...x, url: e.target.value } : x
                                      ),
                                    })
                                  }
                                />
                              )}
                              {b.type === 'PHONE_NUMBER' && (
                                <TextField
                                  size="small"
                                  label="Phone number"
                                  fullWidth
                                  value={b.phone_number ?? ''}
                                  onChange={(e) =>
                                    updateCarouselCard(card.id, {
                                      buttons: card.buttons.map((x, j) =>
                                        j === bi ? { ...x, phone_number: e.target.value } : x
                                      ),
                                    })
                                  }
                                />
                              )}
                              <IconButton size="small" onClick={() => removeButtonFromAllCards(bi)}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                          {card.buttons.length < 2 && (
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={addButtonToAllCards}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add button (all cards)
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    ))}

                    <Stack direction="row" spacing={1}>
                      {form.carouselCards.length < 10 && (
                        <Button variant="outlined" startIcon={<Add />} onClick={addCarouselCard}>
                          Add card
                        </Button>
                      )}
                      <Button
                        color="error"
                        onClick={() => setForm((p) => ({ ...p, carouselCards: [] }))}
                      >
                        Remove carousel
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </Section>
            )}

            {/* Buttons — AUTHENTICATION uses its own OTP button, configured in
                "Code delivery setup" above, not this generic list. Hidden while a
                carousel is active: carousel buttons live on each card instead. */}
            {form.category !== 'AUTHENTICATION' && form.carouselCards.length === 0 && (
            <Section title="Buttons" optional>
              <Stack spacing={1.5}>
                {form.buttons.map((b, i) => {
                  const urlHasVariable = /\{\{\s*\d+\s*\}\}/.test(b.url || '');
                  return (
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    key={i}
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        minWidth: 156, height: 40, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        px: 1.5, borderRadius: 1.5, bgcolor: 'action.hover',
                        fontSize: 13, fontWeight: 600, color: 'text.secondary',
                      }}
                    >
                      {BUTTON_TYPE_LABELS[b.type]}
                    </Box>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flex={1} useFlexGap flexWrap="wrap">
                      {b.type !== 'COPY_CODE' && (
                        <TextField
                          label="Button text"
                          size="small"
                          fullWidth
                          value={b.text}
                          onChange={(e) => updateButton(i, { text: e.target.value })}
                          sx={{ flex: '1 1 200px' }}
                        />
                      )}
                      {b.type === 'URL' && (
                        <Box sx={{ flex: '1 1 220px' }}>
                          <TextField
                            label="Website URL"
                            size="small"
                            fullWidth
                            placeholder="https://example.com/invoices/{{1}}"
                            value={b.url || ''}
                            onChange={(e) => updateButton(i, { url: e.target.value })}
                          />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mt: 0.5 }}
                          >
                            {!urlHasVariable && (
                              <Button
                                size="small"
                                startIcon={<Add fontSize="small" />}
                                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                                onClick={() => {
                                  const base = (b.url || '').replace(/\s+$/, '');
                                  updateButton(i, { url: `${base}{{1}}` });
                                }}
                              >
                                Add variable
                              </Button>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {urlHasVariable
                                ? 'Per-recipient link — set an example value below for Meta\'s review.'
                                : 'Adds {{1}} at the end for a per-recipient link (e.g. each customer\'s own invoice), mapped per-campaign later.'}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      {b.type === 'URL' && urlHasVariable && (
                        <TextField
                          label="Example value"
                          size="small"
                          fullWidth
                          placeholder="e.g. INV-10234"
                          value={b.example || ''}
                          onChange={(e) => updateButton(i, { example: e.target.value })}
                          helperText="Shown to Meta for review — what {{1}} looks like, not the full URL."
                          sx={{ flex: '1 1 200px' }}
                        />
                      )}
                      {b.type === 'PHONE_NUMBER' && (
                        <TextField
                          label="Phone number"
                          size="small"
                          fullWidth
                          value={b.phone_number || ''}
                          onChange={(e) => updateButton(i, { phone_number: e.target.value })}
                          sx={{ flex: '1 1 200px' }}
                        />
                      )}
                      {b.type === 'COPY_CODE' && (
                        <TextField
                          label="Sample coupon code"
                          size="small"
                          fullWidth
                          value={b.example || ''}
                          onChange={(e) => updateButton(i, { example: e.target.value })}
                          helperText="Shown to Meta as an example offer code"
                          sx={{ flex: '1 1 200px' }}
                        />
                      )}
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => removeButton(i)}
                      sx={(theme: Theme) => ({
                        alignSelf: { xs: 'flex-end', md: 'flex-start' },
                        color: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.18) },
                        borderRadius: 1.5,
                      })}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                  );
                })}

                {form.buttons.length < 10 && (
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Add fontSize="small" />}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                      onClick={(e) => setAddButtonAnchor(e.currentTarget)}
                    >
                      Add button
                    </Button>
                    <Menu
                      anchorEl={addButtonAnchor}
                      open={!!addButtonAnchor}
                      onClose={() => setAddButtonAnchor(null)}
                    >
                      <MenuItem onClick={() => addButton('QUICK_REPLY')}>Quick reply</MenuItem>
                      <MenuItem onClick={() => addButton('URL')}>Visit website</MenuItem>
                      <MenuItem onClick={() => addButton('PHONE_NUMBER')}>Call phone number</MenuItem>
                      <MenuItem disabled={COPY_CODE_COUNT >= 1} onClick={() => addButton('COPY_CODE')}>
                        Copy offer code
                      </MenuItem>
                    </Menu>
                  </Box>
                )}
              </Stack>
            </Section>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Preview
            </Typography>
            <TemplatePreview form={form} mediaPreviewUrl={mediaPreviewUrl} />
          </Box>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
        {/* Draft-save isn't offered when editing an existing template — it already
            exists on Meta; the primary action is the edit itself. */}
        {!isExistingEdit && (
          <Button
            variant="outlined"
            onClick={guard('whatsappEnabled', handleSaveDraft)}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 3 }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save as Draft'}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={guard('whatsappEnabled', handleSubmit)}
          disabled={saving || (isExistingEdit && editNoChanges)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 3 }}
        >
          {saving ? (
            <CircularProgress size={20} />
          ) : isExistingEdit ? (
            editContentChanged ? 'Submit for Review' : editMediaStaged ? 'Update Media' : 'No Changes'
          ) : (
            'Submit for Review'
          )}
        </Button>
      </Stack>
    </Box>
  );
};

export default WhatsAppTemplateEditor;
